import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Mail,
  MessageSquare,
  Phone,
  Plus,
  StickyNote,
  CheckSquare,
  Users,
} from 'lucide-react';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Input';
import { Badge } from '@/components/ui/StatusBadge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Banner } from '@/components/ui/Banner';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { useAuthStore } from '@/stores/auth';
import {
  eventsApi,
  EVENT_TYPE_LABELS,
  eventDotTone,
  isTaskOverdue,
  TASK_STATUS_LABELS,
  type EventItem,
  type EventType,
} from '@/services/events.api';
import { EventFormModal } from './EventFormModal';
import { cn, truncateAction } from '@/lib/utils';

interface Props {
  prospectId: string;
  opportunityId?: string;
  compact?: boolean;
}

const TYPE_ICONS: Record<EventType, React.ElementType> = {
  call: Phone,
  meeting: Users,
  task: CheckSquare,
  email: Mail,
  sms: MessageSquare,
  note: StickyNote,
};

export function TimelineSection({ prospectId, opportunityId, compact = false }: Props) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState<EventItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<EventType>('note');
  // Événement sélectionné pour la vue détail/édition. `null` par défaut ;
  // positionné quand l'utilisateur clique sur un item de la timeline.
  const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);

  async function load() {
    try {
      const res = await eventsApi.list(token, {
        prospect_id: prospectId,
        ...(opportunityId ? { opportunity_id: opportunityId } : {}),
        limit: 50,
      });
      setItems(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospectId, opportunityId]);

  const filtered = useMemo(() => {
    if (!items) return [];
    return filterType ? items.filter((e) => e.type === filterType) : items;
  }, [items, filterType]);

  function openForType(type: EventType) {
    setModalType(type);
    setModalOpen(true);
  }

  async function handleCreate(payload: Parameters<typeof eventsApi.create>[1]) {
    await eventsApi.create(token, payload);
    toast({ tone: 'success', message: 'Événement créé' });
    setModalOpen(false);
    await load();
  }

  async function handleComplete(id: string) {
    try {
      await eventsApi.complete(token, id);
      toast({ tone: 'success', message: 'Tâche marquée comme faite' });
      await load();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Action impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  async function handleDelete(id: string) {
    const ok = await confirm({
      title: 'Supprimer cet événement ?',
      variant: 'danger',
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    try {
      await eventsApi.delete(token, id);
      toast({ tone: 'success', message: 'Supprimé' });
      await load();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Suppression impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  return (
    <Card>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <CardTitle>Timeline</CardTitle>
          <CardDescription>
            {items === null ? '…' : `${filtered.length} événement${filtered.length > 1 ? 's' : ''}`}
          </CardDescription>
        </div>
        <QuickAddMenu onPick={openForType} />
      </div>

      {!compact && (
        <div className="mb-3 flex flex-wrap gap-2">
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-auto text-xs"
          >
            <option value="">Tous types</option>
            {(Object.keys(EVENT_TYPE_LABELS) as EventType[]).map((t) => (
              <option key={t} value={t}>
                {EVENT_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
        </div>
      )}

      {error && <Banner tone="danger">{error}</Banner>}

      {items === null && !error && <Skeleton className="h-40 w-full" />}

      {items !== null && filtered.length === 0 && (
        <EmptyState
          title="Aucun événement"
          description="Ajoutez un appel, un RDV, une tâche ou une note."
        />
      )}

      {filtered.length > 0 && (
        <ol className="relative ml-3 space-y-4 border-l border-gray-200 pl-5">
          {filtered.map((ev) => (
            <TimelineItem
              key={ev.id}
              ev={ev}
              onOpen={() => setSelectedEvent(ev)}
              onComplete={() => handleComplete(ev.id)}
              onDelete={() => handleDelete(ev.id)}
            />
          ))}
        </ol>
      )}

      {modalOpen && (
        <EventFormModal
          prospectId={prospectId}
          opportunityId={opportunityId}
          defaultType={modalType}
          onClose={() => setModalOpen(false)}
          onSubmit={handleCreate}
        />
      )}

      {selectedEvent && (
        <EventFormModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onChange={() => {
            void load();
          }}
        />
      )}
    </Card>
  );
}

// Tailwind JIT ne résout pas les classes dynamiques - on mappe explicitement
// chaque tone à ses classes statiques (parsable au build).
const DOT_STYLES: Record<
  'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'purple',
  string
> = {
  neutral: 'bg-gray-100 text-gray-600',
  accent: 'bg-accent-50 text-accent-600',
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  danger: 'bg-danger-50 text-danger-600',
  // Violet Stripe historique (#635BFF) - réservé aux événements d'appel.
  purple: 'bg-[#EEEDFF] text-[#5851DB]',
};

function TimelineItem({
  ev,
  onOpen,
  onComplete,
  onDelete,
}: {
  ev: EventItem;
  onOpen: () => void;
  onComplete: () => void;
  onDelete: () => void;
}) {
  const Icon = TYPE_ICONS[ev.type];
  const overdue = isTaskOverdue(ev);
  // Tone dot : utilise la nouvelle règle done=success / not-done=danger pour
  // les tâches. Les autres types gardent leur tone statique (meeting=accent,
  // call=purple, note/email/sms=neutral).
  const dotTone = eventDotTone(ev);
  return (
    <li className="relative">
      <span
        className={cn(
          'absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white',
          DOT_STYLES[dotTone],
        )}
      >
        <Icon className="h-3 w-3" />
      </span>
      <div className="flex items-start justify-between gap-3">
        {/* Zone cliquable principale : ouvre la modale de détail. Les
            quick-actions (Fait / ×) restent en dehors - ça évite à la fois
            d'imbriquer des boutons (invalide HTML) et de devoir gérer un
            stopPropagation fragile. */}
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 cursor-pointer rounded-md text-left transition-colors hover:bg-gray-50"
          aria-label={`Ouvrir ${EVENT_TYPE_LABELS[ev.type]} - ${ev.title ?? defaultTitle(ev)}`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={dotTone}>{EVENT_TYPE_LABELS[ev.type]}</Badge>
            {/* Badge de statut pour les entités task-like (refonte 2026-05-21,
                Q3 : meetings task-like). Permet de scanner en un coup d'œil
                les RDV passés non encore validés vs les RDV effectués. */}
            {(ev.type === 'task' || ev.type === 'meeting') && ev.taskStatus && (
              <Badge tone={ev.taskStatus === 'done' ? 'success' : 'neutral'}>
                {TASK_STATUS_LABELS[ev.taskStatus]}
              </Badge>
            )}
            {overdue && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 text-[11px] font-medium',
                  // Décision produit 2026-05-22 Q1 : tâche en retard reste
                  // rouge, RDV en retard orange (distinction visuelle).
                  ev.type === 'meeting' ? 'text-warning-600' : 'text-danger-600',
                )}
              >
                <AlertTriangle className="h-3 w-3" />
                En retard
              </span>
            )}
            {ev.opportunity && (
              <span className="text-text-muted font-mono text-[11px]">
                {ev.opportunity.reference}
              </span>
            )}
            {ev.prospectSequence && (
              <Badge tone="accent">Séquence · {ev.prospectSequence.sequence.name}</Badge>
            )}
          </div>
          <div className="text-brand mt-1 text-sm font-medium">
            {truncateAction(ev.title) || defaultTitle(ev)}
          </div>
          {ev.description && (
            <div className="text-text-primary mt-0.5 line-clamp-2 text-xs">{ev.description}</div>
          )}
          <div className="text-text-muted mt-1 text-[11px]">
            {/* Date effective de l'event : scheduledAt (call/meeting/email) >
                taskDueDate (task) > createdAt (fallback). Un mail reçu hier et
                saisi aujourd'hui affichera "hier" — et apparaîtra à sa vraie
                place chronologique grâce au tri backend aligné. */}
            {formatRelative(ev.scheduledAt ?? ev.taskDueDate ?? ev.createdAt)} ·{' '}
            {ev.creator.firstName} {ev.creator.lastName}
            {ev.assignee && ev.assignee.id !== ev.creator.id && (
              <>
                {' '}
                · → {ev.assignee.firstName} {ev.assignee.lastName}
              </>
            )}
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-1">
          {ev.type === 'task' && ev.taskStatus !== 'done' && (
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Check className="h-3 w-3" />}
              onClick={onComplete}
            >
              Fait
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onDelete}>
            ×
          </Button>
        </div>
      </div>
    </li>
  );
}

function QuickAddMenu({ onPick }: { onPick: (t: EventType) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button
        variant="primary"
        size="sm"
        leadingIcon={<Plus className="h-3.5 w-3.5" />}
        onClick={() => setOpen((v) => !v)}
      >
        Ajouter
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} role="presentation" />
          <div className="border-border-subtle shadow-popover absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-md border bg-white">
            {(Object.keys(EVENT_TYPE_LABELS) as EventType[]).map((t) => {
              const Icon = TYPE_ICONS[t];
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    onPick(t);
                    setOpen(false);
                  }}
                  className="text-brand flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-gray-50"
                >
                  <Icon className="h-3.5 w-3.5" />
                  {EVENT_TYPE_LABELS[t]}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// --- utilitaires ------------------------------------------------------------

function defaultTitle(ev: EventItem): string {
  switch (ev.type) {
    case 'call':
      return ev.callDirection === 'inbound' ? 'Appel entrant' : 'Appel sortant';
    case 'meeting':
      return ev.meetingType === 'video' ? 'RDV visio' : 'RDV';
    case 'task':
      return 'Tâche';
    case 'email':
      return 'Email';
    case 'sms':
      return 'SMS';
    case 'note':
      return 'Note';
  }
}

/**
 * Date relative courte, en français. `Intl.RelativeTimeFormat` supporté
 * nativement par tous les navigateurs cibles.
 */
const rtf = new Intl.RelativeTimeFormat('fr-FR', { numeric: 'auto' });
export function formatRelative(isoDate: string): string {
  const delta = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.round(delta / 60_000);
  if (Math.abs(minutes) < 60) return rtf.format(-minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return rtf.format(-hours, 'hour');
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 30) return rtf.format(-days, 'day');
  const months = Math.round(days / 30);
  if (Math.abs(months) < 12) return rtf.format(-months, 'month');
  const years = Math.round(months / 12);
  return rtf.format(-years, 'year');
}
